const template = document.createElement('template');

template.innerHTML = `
  <style>
    :host {
      --lines: 3;
      --fade-height: 3em;
      --fade-color: #fff;
      --link-color: #1f6feb;
      --link-underline: underline;

      display: block;
      position: relative;
    }

    .content {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      -webkit-line-clamp: var(--lines);
      line-clamp: var(--lines);
    }

    :host([expanded]) .content {
      display: block;
      -webkit-box-orient: initial;
      -webkit-line-clamp: none;
      line-clamp: none;
      overflow: visible;
    }

    .fade {
      position: absolute;
      inset-inline: 0;
      bottom: 2.2em;
      height: var(--fade-height);
      pointer-events: none;
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0), var(--fade-color) 85%);
      display: none;
    }
    :host([has-overflow]):not([expanded]) .fade { display: block; }

    .toggle {
      appearance: none;
      border: none;
      background: none;
      color: var(--link-color);
      text-decoration: var(--link-underline);
      padding: 0;
      margin-top: 0.25rem;
      font: inherit;
      cursor: pointer;
    }
    .toggle:focus-visible {
      outline: 2px solid var(--link-color);
      outline-offset: 2px;
    }
  </style>

  <div class="content" id="tc-content" part="content">
    <slot></slot>
  </div>

  <div class="fade" part="fade" aria-hidden="true"></div>

  <button class="toggle" part="toggle" id="tc-toggle"
          type="button" aria-controls="tc-content" aria-expanded="false">
    Show more
  </button>
`;

let instanceId = 0;

class TextClampElement extends HTMLElement {
  static get observedAttributes(): Array<keyof TextClampElementAttributes> {
    return ['lines', 'more-label', 'less-label', 'expanded', 'fade-color'];
  }

  private shadowRootRef: ShadowRoot;
  private contentEl: HTMLElement;
  private toggleButton: HTMLButtonElement;
  private fadeEl: HTMLElement;
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private moreLabel = 'Show more';
  private lessLabel = 'Show less';
  private suppressExpandedCallback = false;
  private isMeasuring = false;

  constructor() {
    super();

    this.shadowRootRef = this.attachShadow({ mode: 'open' });
    this.shadowRootRef.appendChild(template.content.cloneNode(true));

    const content = this.shadowRootRef.querySelector<HTMLElement>('.content');
    const toggle = this.shadowRootRef.querySelector<HTMLButtonElement>('.toggle');
    const fade = this.shadowRootRef.querySelector<HTMLElement>('.fade');

    if (!content || !toggle || !fade) {
      throw new Error('text-clamp: template setup failed');
    }

    this.contentEl = content;
    this.toggleButton = toggle;
    this.fadeEl = fade;

    const idSuffix = `tc-${++instanceId}`;
    this.contentEl.id = `tc-content-${idSuffix}`;
    this.toggleButton.setAttribute('aria-controls', this.contentEl.id);

    this.moreLabel = this.getAttribute('more-label') || this.moreLabel;
    this.lessLabel = this.getAttribute('less-label') || this.lessLabel;

    this.toggleButton.addEventListener('click', () => {
      this.toggle();
    });
  }

  connectedCallback(): void {
    const linesAttr = this.getAttribute('lines');
    const parsedLines = Number.parseInt(linesAttr ?? '3', 10);
    this.style.setProperty('--lines', Number.isFinite(parsedLines) ? `${parsedLines}` : '3');

    const fadeColor = this.getAttribute('fade-color');
    if (fadeColor) {
      this.style.setProperty('--fade-color', fadeColor);
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this.isMeasuring) {
        return;
      }
      this.updateOverflowState();
    });
    this.resizeObserver.observe(this.contentEl);

    this.mutationObserver = new MutationObserver(() => {
      if (this.isMeasuring) {
        return;
      }
      this.updateOverflowState();
    });
    this.mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    if (document.fonts && 'ready' in document.fonts) {
      document.fonts.ready.then(() => {
        this.updateOverflowState();
      }).catch(() => {
        this.updateOverflowState();
      });
    }

    requestAnimationFrame(() => {
      this.updateOverflowState();
    });
  }

  disconnectedCallback(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  attributeChangedCallback(name: keyof TextClampElementAttributes, _oldValue: string | null, newValue: string | null): void {
    if (name === 'lines') {
      const parsedLines = Number.parseInt(newValue ?? '3', 10);
      this.style.setProperty('--lines', Number.isFinite(parsedLines) ? `${parsedLines}` : '3');
      this.updateOverflowState();
      return;
    }

    if (name === 'more-label') {
      this.moreLabel = newValue || 'Show more';
      if (!this.expanded) {
        this.toggleButton.textContent = this.moreLabel;
      }
      return;
    }

    if (name === 'less-label') {
      this.lessLabel = newValue || 'Show less';
      if (this.expanded) {
        this.toggleButton.textContent = this.lessLabel;
      }
      return;
    }

    if (name === 'expanded') {
      if (this.suppressExpandedCallback) {
        return;
      }

      this.syncToggleState();
      this.updateOverflowState();
      return;
    }

    if (name === 'fade-color' && newValue) {
      this.style.setProperty('--fade-color', newValue);
    }
  }

  get expanded(): boolean {
    return this.hasAttribute('expanded');
  }

  set expanded(value: boolean) {
    if (value) {
      this.setAttribute('expanded', '');
    } else {
      this.removeAttribute('expanded');
    }
  }

  get lines(): number {
    return Number.parseInt(this.getAttribute('lines') ?? '3', 10);
  }

  set lines(value: number) {
    this.setAttribute('lines', `${value}`);
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  private updateOverflowState(): void {
    const wasExpanded = this.expanded;

    if (wasExpanded) {
      this.suppressExpandedCallback = true;
      this.isMeasuring = true;
      this.removeAttribute('expanded');
    }

    requestAnimationFrame(() => {
      const overflowing = this.contentEl.scrollHeight > this.contentEl.clientHeight + 1;

      if (overflowing) {
        this.setAttribute('has-overflow', '');
        this.toggleButton.hidden = false;
      } else {
        this.removeAttribute('has-overflow');
        this.toggleButton.hidden = true;
      }

      if (wasExpanded) {
        this.setAttribute('expanded', '');
      }

  this.suppressExpandedCallback = false;
      this.isMeasuring = false;
  this.syncToggleState();

      this.dispatchEvent(new CustomEvent('overflowchange', {
        detail: { hasOverflow: overflowing },
        bubbles: false,
        composed: true,
      }));
    });
  }

  private syncToggleState(): void {
    const isExpanded = this.expanded;
    this.toggleButton.setAttribute('aria-expanded', String(isExpanded));
    this.toggleButton.textContent = isExpanded ? this.lessLabel : this.moreLabel;
  }
}

type TextClampElementAttributes = {
  lines: string;
  'more-label': string;
  'less-label': string;
  expanded: string;
  'fade-color': string;
};

declare global {
  interface HTMLElementTagNameMap {
    'text-clamp': TextClampElement;
  }
}

if (!window.customElements.get('text-clamp')) {
  window.customElements.define('text-clamp', TextClampElement);
}

export {};
